import React from 'react';
import { Space, Typography, Empty, Alert, Card, Statistic, Row, Col } from 'antd';
import { ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ConfigData } from '../types';
import { ExecutionStepCard } from './ExecutionStepCard';

const { Title, Text } = Typography;

interface ExecutionStepsViewerProps {
  data: ConfigData;
}

export const ExecutionStepsViewer: React.FC<ExecutionStepsViewerProps> = ({ data }) => {
  if (!data?.config_steps || data.config_steps.length === 0) {
    return (
      <Empty
        description="No execution steps defined"
        style={{ padding: '40px 0' }}
      />
    );
  }

  const highRiskSteps = data.config_steps.filter(s => s.explanation?.risk_level === 'high').length;
  const mediumRiskSteps = data.config_steps.filter(s => s.explanation?.risk_level === 'medium').length;
  const lowRiskSteps = data.config_steps.filter(s => s.explanation?.risk_level === 'low').length;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Description */}
      {data.description && (
        <Alert
          message={<span style={{ fontSize: '14px', fontWeight: 600 }}>📋 Execution Plan</span>}
          description={data.description}
          type="info"
          showIcon
          style={{
            borderRadius: '8px',
            border: '1px solid #91d5ff'
          }}
        />
      )}

      {/* Statistics Summary */}
      <Card
        title={<span style={{ fontSize: '16px', fontWeight: 600 }}>Configuration Summary</span>}
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #f0f0f0'
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <div style={{
              background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #91d5ff',
              transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', fontWeight: 500 }}>Total Steps</span>}
                value={data.config_steps.length}
                prefix={<ThunderboltOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              />
            </div>
          </Col>
          {highRiskSteps > 0 && (
            <Col span={6}>
              <div style={{
                background: 'linear-gradient(135deg, #fff2f0 0%, #fff1f0 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #ffccc7',
                transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 77, 79, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <Statistic
                  title={<span style={{ fontSize: '13px', fontWeight: 500 }}>High Risk</span>}
                  value={highRiskSteps}
                  valueStyle={{ color: '#ff4d4f', fontWeight: 600 }}
                />
              </div>
            </Col>
          )}
          {mediumRiskSteps > 0 && (
            <Col span={6}>
              <div style={{
                background: 'linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #ffe58f',
                transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(250, 173, 20, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <Statistic
                  title={<span style={{ fontSize: '13px', fontWeight: 500 }}>Medium Risk</span>}
                  value={mediumRiskSteps}
                  valueStyle={{ color: '#faad14', fontWeight: 600 }}
                />
              </div>
            </Col>
          )}
          {lowRiskSteps > 0 && (
            <Col span={6}>
              <div style={{
                background: 'linear-gradient(135deg, #f6ffed 0%, #f0f5ff 100%)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #b7eb8f',
                transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <Statistic
                  title={<span style={{ fontSize: '13px', fontWeight: 500 }}>Low Risk</span>}
                  value={lowRiskSteps}
                  valueStyle={{ color: '#52c41a', fontWeight: 600 }}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                />
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {/* Step Cards */}
      <div>
        <Text type="secondary" style={{ fontSize: '13px', marginBottom: '12px', display: 'block' }}>
          💡 Click on any step to expand details
        </Text>
        {data.config_steps.map((step, index) => (
          <ExecutionStepCard
            key={index}
            step={step}
            stepIndex={index}
            allSteps={data.config_steps}
          />
        ))}
      </div>
    </Space>
  );
};

export default ExecutionStepsViewer;
